import { cn } from "@drpcrd/utils/shared";
import { Nullable } from "@drpcrd/utils";
import { formatCdnImg } from "@drpcrd/utils/formatters";
import { Building } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { DefaultCampaignLogo } from "./icons";

type Props = {
  /** The individual sender's own Clerk profile photo — already an absolute
   * Clerk-hosted URL, used as-is. Falls back to a generic building icon
   * when unavailable (e.g. older messages sent before this was captured). */
  avatarUrl?: Nullable<string>;
  /** The sender's talent-agency logo. Already-absolute URLs (e.g. a Clerk
   * org image) are used as-is; relative paths are routed through the CDN.
   * Empty means no logo (the badge falls back to the default campaign
   * icon). */
  logoUrl?: Nullable<string>;
  name?: string;
  className?: string;
};

/**
 * Avatar for a talent-agent chat sender — the agent's own profile photo,
 * with their agency's logo overlaid as a small badge overflowing the
 * bottom-right corner (so a message reads as both "who" and "which
 * agency"). Mirrors `CreatorAvatar`'s country-flag badge pattern. Brand
 * senders use `CompanyAvatar` instead — the workspace logo alone, as in the
 * brand app's sidebar.
 */
export const CompanySenderAvatar = ({
  avatarUrl,
  logoUrl,
  name,
  className,
}: Props) => {
  // `Campaigns.logo` defaults to "" rather than null — treat that as no
  // logo, otherwise `formatCdnImg` hands the badge the bare CDN host and
  // the image errors out.
  const badgeSrc = !logoUrl
    ? undefined
    : logoUrl.startsWith("http")
      ? logoUrl
      : formatCdnImg(logoUrl);

  return (
    <div className={cn("relative h-4 w-4 shrink-0", className)}>
      <Avatar className="h-4 w-4">
        <AvatarImage src={avatarUrl ?? undefined} alt={name ?? ""} />
        <AvatarFallback className="bg-primary-50">
          <Building className="text-primary-500 h-[60%] w-[60%]" />
        </AvatarFallback>
      </Avatar>
      <Avatar className="border-background absolute -right-1 -bottom-1 h-2.5 w-2.5 border">
        <AvatarImage src={badgeSrc} alt={name ?? ""} />
        <AvatarFallback className="bg-neutral-200">
          <DefaultCampaignLogo className="h-[60%] w-[60%]" />
        </AvatarFallback>
      </Avatar>
    </div>
  );
};
